/* SmartEvaluate — PRIVA Extracted Intelligence (consultant review).
 *
 * This is purely a PRESENTATION layer over the parsed extraction JSON. The
 * backend extraction stores the raw parsed object as-is under
 * `assessment.rfp_extraction_result`; nothing here mutates it.
 *
 * Layout (per product spec):
 *   A. RFP Snapshot
 *   B. Scope Summary
 *   C. Warehouse Operations (per-op cards with View Details)
 *   D. Volumes & Users
 *   E. Integration Requirements
 *   F. Technical & Mobility Requirements
 *   G. SAP Solution Signals
 * Bottom: Mark Extraction Reviewed  +  Convert to SmartEvaluate Structure
 *
 * Boundaries:
 *   - No raw JSON visible. No technical/internal extraction keys shown.
 *   - Booleans rendered as human labels (Required / Not Required / In Scope / …).
 *   - Confidence shown ONLY as a small section-level badge.
 *   - All low-value details hidden behind a per-operation View Details toggle.
 *
 * The renderer is schema-tolerant — missing sections degrade silently.
 */
import { useMemo, useRef, useState } from "react";
import {
  Sparkles,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  Circle,
  Info,
  Building2,
  Boxes,
  Truck,
  PackageCheck,
  ClipboardList,
  Plug,
  Server,
  ShieldCheck,
  Smartphone,
  Brain,
  BadgeCheck,
  ArrowRight,
  Upload,
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  patchRfpSubmission,
  uploadRfpCustomerResponses,
  clearRfpCustomerResponses,
} from "../lib/rfpApi";
import {
  OpenQuestionsSection,
  RisksAssumptionsSection,
} from "./RfpPrivaQuestions";
import RfpConversionPreview from "./RfpConversionPreview";

/* -------------------------------------------------------------------------- */
/*  Utilities                                                                  */
/* -------------------------------------------------------------------------- */

// Technical keys that are never shown to the consultant. Internal scoring,
// debug, or parsing artefacts go here.
const HIDDEN_KEYS = new Set([
  "confidence",
  "extraction_confidence",
  "extraction_vector_score",
  "extraction_score",
  "confidence_score",
  "confidence_temperature",
  "warehouse_count_detected",
  "source_documents",
  "raw_response",
  "extracted_by",
  "extracted_at",
  "schema_version",
]);

function isEmpty(v) {
  if (v === null || v === undefined) return true;
  if (typeof v === "string") return v.trim() === "";
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === "object") return Object.keys(v).length === 0;
  return false;
}

function humanize(text) {
  if (text === null || text === undefined) return "";
  const s = String(text).replace(/[_-]+/g, " ").trim();
  // Preserve common acronyms.
  return s
    .toLowerCase()
    .split(" ")
    .map((w) => {
      if (!w) return "";
      const acronyms = ["sap", "ewm", "edi", "rf", "hht", "3pl", "wms", "bol", "dr", "kpi", "kpis", "fmcg"];
      if (acronyms.includes(w)) return w.toUpperCase();
      return w[0].toUpperCase() + w.slice(1);
    })
    .join(" ");
}

/** Try a sequence of paths and return the first non-empty value found.
 *  Path syntax: dot-separated keys. Array indexes are not supported here —
 *  callers should pre-flatten arrays. */
function pick(obj, ...paths) {
  if (!obj || typeof obj !== "object") return undefined;
  for (const path of paths) {
    let cur = obj;
    let ok = true;
    for (const part of String(path).split(".")) {
      if (cur && typeof cur === "object" && part in cur) {
        cur = cur[part];
      } else {
        ok = false;
        break;
      }
    }
    if (ok && !isEmpty(cur)) return cur;
  }
  return undefined;
}

function boolLabel(v, mode = "requirement") {
  if (v === true || v === "true" || v === "yes" || v === "Yes" || v === 1) {
    if (mode === "scope") return "In Scope";
    if (mode === "yesno") return "Yes";
    return "Required";
  }
  if (v === false || v === "false" || v === "no" || v === "No" || v === 0) {
    if (mode === "scope") return "Out of Scope";
    if (mode === "yesno") return "No";
    return "Not Required";
  }
  return null;
}

/* -------------------------------------------------------------------------- */
/*  Atomic UI primitives                                                       */
/* -------------------------------------------------------------------------- */

function ConfidenceBadge({ value }) {
  if (!value) return null;
  const v = String(value).toUpperCase();
  const color =
    v === "HIGH"
      ? "bg-[#ECFDF5] text-[#065F46] border-[#065F46]/15"
      : v === "MEDIUM"
      ? "bg-[#FEF3C7] text-[#92400E] border-[#92400E]/15"
      : "bg-[#FEE2E2] text-[#991B1B] border-[#991B1B]/15";
  return (
    <span
      data-testid="rfp-priva-confidence-badge"
      className={[
        "inline-flex items-center text-[10px] font-semibold uppercase tracking-wide rounded-sm px-2 py-0.5 border",
        color,
      ].join(" ")}
    >
      Confidence: {v}
    </span>
  );
}

function StatusPill({ label, tone = "neutral" }) {
  const tones = {
    inScope:
      "bg-[#ECFDF5] text-[#065F46] border-[#065F46]/15",
    outOfScope:
      "bg-[#F3F4F6] text-[#6B7280] border-[#E5E7EB]",
    neutral:
      "bg-[#E0F2FE] text-[#025979] border-[#025979]/15",
    accent:
      "bg-[#F3E8FF] text-[#6D28D9] border-[#6D28D9]/15",
  };
  return (
    <span
      className={[
        "inline-flex items-center text-[11px] font-semibold uppercase tracking-wide rounded-sm px-2 py-0.5 border",
        tones[tone] || tones.neutral,
      ].join(" ")}
    >
      {label}
    </span>
  );
}

function Field({ label, value, testId }) {
  if (isEmpty(value)) return null;
  let display;
  if (typeof value === "boolean") display = boolLabel(value) || (value ? "Yes" : "No");
  else if (Array.isArray(value)) display = value.filter((x) => !isEmpty(x)).join(", ");
  else if (typeof value === "object") display = null;
  else display = String(value);
  if (isEmpty(display)) return null;

  return (
    <div className="min-w-0" data-testid={testId}>
      <dt className="text-[11px] font-medium text-[#6B7280] uppercase tracking-wide">
        {label}
      </dt>
      <dd className="mt-0.5 text-sm text-[#1F2937] leading-relaxed break-words">
        {display}
      </dd>
    </div>
  );
}

function BulletList({ items, max, testId }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  const list = max ? items.slice(0, max) : items;
  return (
    <ul
      data-testid={testId}
      className="list-disc ml-5 space-y-1"
    >
      {list.map((it, i) => (
        <li
          key={i}
          className="text-sm text-[#1F2937] leading-relaxed"
        >
          {typeof it === "string" ? it : humanize(JSON.stringify(it).slice(0, 200))}
        </li>
      ))}
    </ul>
  );
}

/** Inline disclosure used inside operation cards. */
function DetailsToggle({ label = "View Details", children, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#025979] hover:text-[#03738C] uppercase tracking-wide transition-colors"
      >
        {open ? (
          <ChevronDown className="w-3.5 h-3.5" strokeWidth={1.75} />
        ) : (
          <ChevronRight className="w-3.5 h-3.5" strokeWidth={1.75} />
        )}
        {open ? "Hide Details" : label}
      </button>
      {open && (
        <div className="mt-2 pt-2 border-t border-[#E5E7EB] space-y-2.5">
          {children}
        </div>
      )}
    </div>
  );
}

function SectionShell({ id, icon: Icon, title, confidence, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section
      data-testid={`rfp-priva-${id}-section`}
      className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`rfp-priva-${id}-toggle`}
        className="w-full flex items-center justify-between gap-3 px-5 py-3.5 sm:px-6 hover:bg-[#F8FAFC] transition-colors"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-8 h-8 rounded-sm bg-[#E0F2FE] flex items-center justify-center shrink-0">
            <Icon className="w-4 h-4 text-[#025979]" strokeWidth={1.75} />
          </div>
          <h4
            className="text-[13px] font-semibold text-[#111827] uppercase tracking-wider"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            {title}
          </h4>
          {confidence && <ConfidenceBadge value={confidence} />}
        </div>
        {open ? (
          <ChevronDown className="w-4 h-4 text-[#9CA3AF]" />
        ) : (
          <ChevronRight className="w-4 h-4 text-[#9CA3AF]" />
        )}
      </button>
      {open && (
        <div className="px-5 py-4 sm:px-6 sm:py-5 border-t border-[#E5E7EB]">
          {children}
        </div>
      )}
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section A — RFP Snapshot                                                   */
/* -------------------------------------------------------------------------- */
function SnapshotSection({ result }) {
  const ci = pick(result, "client_info", "client_profile") || {};
  const pc =
    pick(result, "program_context", "transformation_context", "rfp_subject_entity") || {};
  const sites = pick(result, "sites") || [];
  const firstSite = Array.isArray(sites) && sites.length > 0 ? sites[0] : {};

  const fields = [
    {
      label: "End Client",
      value:
        pick(ci, "actual_end_client_identified", "end_client", "company_name") ||
        pick(result, "client_profile.company_name"),
    },
    { label: "Industry", value: pick(ci, "industry") },
    {
      label: "Region",
      value:
        pick(ci, "country_region", "country", "region") ||
        pick(firstSite, "country", "region"),
    },
    {
      label: "Program Type",
      value: humanize(pick(pc, "program_type", "engagement_type")),
    },
    { label: "RFP Stage", value: humanize(pick(pc, "rfp_stage", "stage")) },
    {
      label: "Deployment Preference",
      value: pick(
        pc,
        "deployment_preference",
        "deployment_model",
        "preferred_deployment"
      ),
    },
    {
      label: "Program Scale",
      value: humanize(pick(pc, "program_scale", "scale", "size")),
    },
    {
      label: "SAP Integration Required",
      value: boolLabel(pick(pc, "sap_integration_required", "sap_required")),
    },
  ].filter((f) => !isEmpty(f.value));

  const sapContext = pick(pc, "sap_system_context");
  const driver = pick(pc, "transformation_driver", "rfp_objective", "objective");

  if (fields.length === 0 && !sapContext && !driver) return null;

  return (
    <SectionShell
      id="snapshot"
      icon={Building2}
      title="RFP Snapshot"
      confidence={pick(pc, "confidence")}
    >
      {driver && (
        <p
          data-testid="rfp-priva-snapshot-driver"
          className="text-sm text-[#1F2937] leading-relaxed mb-4"
        >
          {driver}
        </p>
      )}
      <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
        {fields.map((f) => (
          <Field
            key={f.label}
            label={f.label}
            value={f.value}
            testId={`rfp-priva-snapshot-${f.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          />
        ))}
      </dl>
      {sapContext && (
        <div className="mt-4 pt-4 border-t border-[#E5E7EB]">
          <dt className="text-[11px] font-medium text-[#6B7280] uppercase tracking-wide">
            SAP System Context
          </dt>
          <dd className="mt-0.5 text-sm text-[#1F2937] leading-relaxed">
            {sapContext}
          </dd>
        </div>
      )}
    </SectionShell>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section B — Scope Summary                                                  */
/* -------------------------------------------------------------------------- */
function ScopeSection({ result }) {
  // Prefer explicit scope_summary block; fall back to deriving from
  // warehouse_operations.*.in_scope when only that exists.
  const scope = pick(result, "scope_summary", "functional_scope_summary") || {};
  let inScope =
    pick(scope, "in_scope", "in_scope_items", "in_scope_areas") ||
    deriveScopeList(result, true);
  let outOfScope =
    pick(scope, "out_of_scope", "out_of_scope_items", "out_of_scope_areas") ||
    deriveScopeList(result, false);
  const exclusions =
    pick(result, "scope_exclusions", "explicit_exclusions") ||
    pick(scope, "exclusions", "key_exclusions");

  if (isEmpty(inScope) && isEmpty(outOfScope) && isEmpty(exclusions)) return null;

  return (
    <SectionShell id="scope" icon={PackageCheck} title="Scope Summary">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {!isEmpty(inScope) && (
          <ScopeBlock
            testId="rfp-priva-scope-in"
            title="In Scope"
            tone="inScope"
            items={normalizeToStrings(inScope)}
          />
        )}
        {!isEmpty(outOfScope) && (
          <ScopeBlock
            testId="rfp-priva-scope-out"
            title="Out of Scope"
            tone="outOfScope"
            items={normalizeToStrings(outOfScope)}
          />
        )}
      </div>
      {!isEmpty(exclusions) && (
        <div className="mt-5 pt-4 border-t border-[#E5E7EB]">
          <p className="text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide mb-2">
            Key Exclusions
          </p>
          <BulletList
            testId="rfp-priva-scope-exclusions"
            items={normalizeToStrings(exclusions)}
          />
        </div>
      )}
    </SectionShell>
  );
}

function ScopeBlock({ title, tone, items, testId }) {
  return (
    <div data-testid={testId}>
      <div className="mb-2">
        <StatusPill label={title} tone={tone} />
      </div>
      <BulletList items={items} />
    </div>
  );
}

function normalizeToStrings(value) {
  if (Array.isArray(value)) return value.map((v) => (typeof v === "string" ? v : humanize(v.label || v.name || v.area || JSON.stringify(v))));
  if (typeof value === "string") return value.split(/[;\n]/).map((s) => s.trim()).filter(Boolean);
  if (value && typeof value === "object") return Object.keys(value).filter((k) => value[k]).map(humanize);
  return [];
}

function deriveScopeList(result, inScopeTruthy) {
  const ops = pick(result, "warehouse_operations") || {};
  if (typeof ops !== "object" || Array.isArray(ops)) return undefined;
  const out = [];
  Object.entries(ops).forEach(([k, v]) => {
    if (!v || typeof v !== "object") return;
    const flag = v.in_scope ?? v.is_in_scope;
    if (flag === inScopeTruthy) out.push(humanize(k));
  });
  return out.length ? out : undefined;
}

/* -------------------------------------------------------------------------- */
/*  Section C — Warehouse Operations                                           */
/* -------------------------------------------------------------------------- */

// Stable canonical order + display titles for the warehouse operations.
const OPERATION_ORDER = [
  { key: "inbound", title: "Inbound" },
  { key: "putaway", title: "Putaway" },
  { key: "replenishment", title: "Replenishment" },
  { key: "picking", title: "Picking" },
  { key: "outbound_dispatch", title: "Outbound / Dispatch", aliases: ["outbound", "dispatch"] },
  { key: "inventory_management", title: "Inventory", aliases: ["inventory"] },
  { key: "quality_management", title: "Quality", aliases: ["quality", "quality_control"] },
  { key: "returns", title: "Returns", aliases: ["reverse_logistics"] },
];

function OperationsSection({ result }) {
  const ops = pick(result, "warehouse_operations") || {};
  if (!ops || typeof ops !== "object") return null;

  const cards = OPERATION_ORDER.map((spec) => {
    let block = ops[spec.key];
    if (isEmpty(block) && Array.isArray(spec.aliases)) {
      for (const a of spec.aliases) {
        if (!isEmpty(ops[a])) {
          block = ops[a];
          break;
        }
      }
    }
    if (isEmpty(block)) return null;
    return { spec, block };
  }).filter(Boolean);

  if (cards.length === 0) return null;

  return (
    <SectionShell id="operations" icon={Boxes} title="Warehouse Operations">
      <div className="space-y-3">
        {cards.map(({ spec, block }) => (
          <OperationCard key={spec.key} spec={spec} block={block} />
        ))}
      </div>
    </SectionShell>
  );
}

function OperationCard({ spec, block }) {
  const inScopeRaw = pick(block, "in_scope", "is_in_scope");
  const inScope = inScopeRaw === true || inScopeRaw === "true" || inScopeRaw === "yes";
  const processes =
    pick(block, "processes", "key_processes", "requirements", "features", "capabilities") || [];
  const topRequirements = (Array.isArray(processes) ? processes : []).slice(0, 5);

  // Detail keys = anything left over after we've consumed the headline ones.
  const consumed = new Set([
    "in_scope",
    "is_in_scope",
    "processes",
    "key_processes",
    "requirements",
    "features",
    "capabilities",
    ...HIDDEN_KEYS,
  ]);
  const detailEntries =
    block && typeof block === "object" && !Array.isArray(block)
      ? Object.entries(block).filter(
          ([k, v]) => !consumed.has(k) && !HIDDEN_KEYS.has(k) && !isEmpty(v)
        )
      : [];

  return (
    <div
      data-testid={`rfp-priva-operation-${spec.key}`}
      className="bg-[#F8FAFC] border border-[#E5E7EB] rounded-sm px-4 py-3.5"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5">
          {inScope ? (
            <CheckCircle2 className="w-4 h-4 text-[#065F46]" strokeWidth={2} />
          ) : (
            <Circle className="w-4 h-4 text-[#9CA3AF]" strokeWidth={2} />
          )}
          <span className="text-sm font-semibold text-[#111827]">
            {spec.title}
          </span>
          <StatusPill
            label={inScope ? "In Scope" : "Out of Scope"}
            tone={inScope ? "inScope" : "outOfScope"}
          />
        </div>
      </div>

      {topRequirements.length > 0 && (
        <div className="mt-3">
          <p className="text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1.5">
            Top Requirements
          </p>
          <BulletList items={topRequirements} />
        </div>
      )}

      {detailEntries.length > 0 && (
        <DetailsToggle testId={`rfp-priva-operation-details-${spec.key}`}>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2.5">
            {detailEntries.map(([k, v]) => (
              <Field
                key={k}
                label={humanize(k)}
                value={normaliseDetailValue(v)}
                testId={`rfp-priva-operation-detail-${spec.key}-${k}`}
              />
            ))}
          </dl>
        </DetailsToggle>
      )}
    </div>
  );
}

function normaliseDetailValue(v) {
  if (typeof v === "boolean") return boolLabel(v);
  if (typeof v === "string" && /^(true|false)$/i.test(v.trim())) return boolLabel(v);
  if (Array.isArray(v)) {
    const flat = v.filter((x) => !isEmpty(x));
    if (flat.every((x) => typeof x === "string" || typeof x === "number")) {
      return flat.slice(0, 6).join(", ") + (flat.length > 6 ? ` (+${flat.length - 6} more)` : "");
    }
    return `${flat.length} item${flat.length === 1 ? "" : "s"}`;
  }
  if (v && typeof v === "object") {
    const entries = Object.entries(v).filter(([k, val]) => !HIDDEN_KEYS.has(k) && !isEmpty(val));
    if (entries.length === 0) return null;
    return entries
      .slice(0, 4)
      .map(([k, val]) => `${humanize(k)}: ${typeof val === "boolean" ? boolLabel(val) : String(val).slice(0, 60)}`)
      .join(" · ");
  }
  return v;
}

/* -------------------------------------------------------------------------- */
/*  Section D — Volumes & Users                                                */
/* -------------------------------------------------------------------------- */
function VolumesSection({ result }) {
  const sites = pick(result, "sites") || [];
  const standalone = pick(
    result,
    "operational_volumes",
    "volumes",
    "reporting_and_kpis"
  );

  // Aggregate across sites (sum numerics) and prefer first-site labels for
  // anything non-numeric.
  const agg = {
    daily_reception: 0,
    daily_dispatch: 0,
    picking_lines: 0,
    concurrent_hht_users: 0,
    total_users: 0,
    throughput_assumptions: null,
  };
  let aggregated = false;
  if (Array.isArray(sites)) {
    sites.forEach((s) => {
      const tv = pick(s, "throughput_volumes") || {};
      const uv = pick(s, "user_volumes") || {};
      const recep = pick(
        tv,
        "daily_reception_volume",
        "daily_inbound",
        "daily_reception",
        "inbound_volume"
      );
      const disp = pick(
        tv,
        "daily_dispatch_volume",
        "daily_outbound",
        "daily_dispatch",
        "outbound_volume"
      );
      const pick_lines = pick(
        tv,
        "picking_lines",
        "daily_picking_lines",
        "picking_volume",
        "pbl_picking_lines"
      );
      const hht = pick(
        uv,
        "concurrent_hht_users",
        "concurrent_users",
        "hht_users"
      );
      const tot = pick(uv, "total_users", "total_warehouse_users", "user_count");
      if (typeof recep === "number") { agg.daily_reception += recep; aggregated = true; }
      if (typeof disp === "number") { agg.daily_dispatch += disp; aggregated = true; }
      if (typeof pick_lines === "number") { agg.picking_lines += pick_lines; aggregated = true; }
      if (typeof hht === "number") { agg.concurrent_hht_users += hht; aggregated = true; }
      if (typeof tot === "number") { agg.total_users += tot; aggregated = true; }
      if (!agg.throughput_assumptions) {
        agg.throughput_assumptions = pick(
          tv,
          "throughput_assumptions",
          "assumptions",
          "notes"
        );
      }
    });
  }
  if (!aggregated && standalone && typeof standalone === "object") {
    agg.daily_reception = pick(standalone, "daily_reception_volume", "daily_inbound");
    agg.daily_dispatch = pick(standalone, "daily_dispatch_volume", "daily_outbound");
    agg.picking_lines = pick(standalone, "picking_volume", "daily_picking_lines");
    agg.concurrent_hht_users = pick(standalone, "concurrent_hht_users");
    agg.total_users = pick(standalone, "total_users");
    agg.throughput_assumptions = pick(standalone, "throughput_assumptions", "assumptions");
    aggregated = !isEmpty(agg);
  }

  if (!aggregated) return null;

  const fmt = (n) => (typeof n === "number" && n > 0 ? n.toLocaleString() : null);
  const fields = [
    { label: "Daily Reception Volume", value: fmt(agg.daily_reception) },
    { label: "Daily Dispatch Volume", value: fmt(agg.daily_dispatch) },
    { label: "Picking Lines", value: fmt(agg.picking_lines) },
    { label: "Concurrent HHT Users", value: fmt(agg.concurrent_hht_users) },
    { label: "Total Warehouse Users", value: fmt(agg.total_users) },
  ].filter((f) => !isEmpty(f.value));

  if (fields.length === 0 && isEmpty(agg.throughput_assumptions)) return null;

  return (
    <SectionShell id="volumes" icon={Truck} title="Volumes & Users">
      {fields.length > 0 && (
        <dl className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-4">
          {fields.map((f) => (
            <Field
              key={f.label}
              label={f.label}
              value={f.value}
              testId={`rfp-priva-volume-${f.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            />
          ))}
        </dl>
      )}
      {!isEmpty(agg.throughput_assumptions) && (
        <div className="mt-4 pt-4 border-t border-[#E5E7EB]">
          <p className="text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1.5">
            Throughput Assumptions
          </p>
          <p className="text-sm text-[#1F2937] leading-relaxed">
            {typeof agg.throughput_assumptions === "string"
              ? agg.throughput_assumptions
              : humanize(JSON.stringify(agg.throughput_assumptions).slice(0, 300))}
          </p>
        </div>
      )}
    </SectionShell>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section E — Integration Requirements                                       */
/* -------------------------------------------------------------------------- */
function IntegrationSection({ result }) {
  const ir = pick(result, "integration_requirements") || {};
  const lp = pick(result, "label_printing");

  const sap = pick(ir, "sap_integration", "sap", "sap_required");
  const edi = pick(ir, "edi", "edi_integration", "edi_required");
  const downstream = pick(
    ir,
    "downstream_systems",
    "store_systems",
    "downstream_store_systems",
    "downstream_integration"
  );
  const threepl = pick(ir, "three_pl_integration", "3pl_integration", "threepl");
  const labelPrinting =
    pick(ir, "label_printing") ||
    (lp && typeof lp === "object" ? lp : null) ||
    (lp && typeof lp === "boolean" ? lp : undefined);
  const excluded = pick(
    ir,
    "explicitly_excluded",
    "explicitly_excluded_integrations",
    "excluded_integrations",
    "out_of_scope_integrations"
  );

  const blocks = [
    { label: "SAP Integration", value: sap, mode: "requirement" },
    { label: "EDI", value: edi, mode: "requirement" },
    { label: "Downstream / Store Systems", value: downstream, mode: "requirement" },
    { label: "3PL Integration", value: threepl, mode: "requirement" },
    { label: "Label Printing", value: labelPrinting, mode: "requirement" },
  ].filter((b) => !isEmpty(b.value));

  if (blocks.length === 0 && isEmpty(excluded)) return null;

  return (
    <SectionShell id="integration" icon={Plug} title="Integration Requirements">
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
        {blocks.map(({ label, value, mode }) => (
          <IntegrationField key={label} label={label} value={value} mode={mode} />
        ))}
      </dl>
      {!isEmpty(excluded) && (
        <div className="mt-5 pt-4 border-t border-[#E5E7EB]">
          <p className="text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide mb-2">
            Explicitly Excluded Integrations
          </p>
          <BulletList
            testId="rfp-priva-integration-excluded"
            items={normalizeToStrings(excluded)}
          />
        </div>
      )}
    </SectionShell>
  );
}

function IntegrationField({ label, value, mode }) {
  let display;
  if (typeof value === "boolean") {
    display = (
      <StatusPill
        label={boolLabel(value, mode)}
        tone={value ? "inScope" : "outOfScope"}
      />
    );
  } else if (Array.isArray(value)) {
    const flat = value.filter((x) => !isEmpty(x));
    display = (
      <BulletList items={flat.map((v) => (typeof v === "string" ? v : humanize(v?.name || v?.label || JSON.stringify(v))))} max={6} />
    );
  } else if (typeof value === "object") {
    const required = pick(value, "required", "is_required", "in_scope");
    const detail = pick(value, "details", "description", "notes", "summary", "processes");
    display = (
      <div className="space-y-1.5">
        {typeof required === "boolean" && (
          <StatusPill
            label={boolLabel(required, mode)}
            tone={required ? "inScope" : "outOfScope"}
          />
        )}
        {!isEmpty(detail) && (
          <p className="text-sm text-[#1F2937] leading-relaxed">
            {typeof detail === "string" ? detail : humanize(JSON.stringify(detail).slice(0, 300))}
          </p>
        )}
      </div>
    );
  } else {
    display = <span className="text-sm text-[#1F2937]">{String(value)}</span>;
  }
  return (
    <div data-testid={`rfp-priva-integration-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <dt className="text-[11px] font-medium text-[#6B7280] uppercase tracking-wide mb-1">
        {label}
      </dt>
      <dd>{display}</dd>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section F — Technical & Mobility Requirements                              */
/* -------------------------------------------------------------------------- */
function TechnicalSection({ result }) {
  const tr = pick(result, "technical_requirements") || {};
  const rf = pick(result, "rf_mobility", "mobility") || {};

  const fields = [
    {
      label: "Deployment Model",
      value: humanize(
        pick(tr, "deployment_model", "deployment", "hosting_model")
      ),
    },
    {
      label: "Availability / DR",
      value:
        pick(tr, "availability", "high_availability", "dr", "disaster_recovery") ||
        pick(tr, "availability_dr"),
    },
    {
      label: "Security",
      value:
        pick(tr, "security", "security_requirements") ||
        pick(tr, "security_compliance"),
    },
    {
      label: "RF / HHT Requirement",
      value: boolLabel(pick(rf, "required", "rf_required", "hht_required")),
    },
    {
      label: "Offline / No-Signal Operation",
      value: boolLabel(
        pick(rf, "offline_required", "no_signal_operation", "offline_capable")
      ),
    },
    {
      label: "Performance Expectations",
      value:
        pick(tr, "performance_expectations", "performance", "sla") ||
        pick(tr, "response_time_sla"),
    },
  ].filter((f) => !isEmpty(f.value));

  if (fields.length === 0) return null;

  return (
    <SectionShell
      id="technical"
      icon={Server}
      title="Technical & Mobility Requirements"
    >
      <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
        {fields.map((f) => (
          <Field
            key={f.label}
            label={f.label}
            value={typeof f.value === "object" ? humanize(JSON.stringify(f.value).slice(0, 200)) : f.value}
            testId={`rfp-priva-tech-${f.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          />
        ))}
      </dl>
    </SectionShell>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section G — SAP Solution Signals                                           */
/* -------------------------------------------------------------------------- */
function SignalsSection({ result }) {
  const sap = pick(result, "sap_solution_signals") || {};
  const vendor = pick(result, "vendor_qualification_signals") || {};
  const complexity =
    pick(result, "complexity_signals", "key_complexity_signals", "inventory_control_signals") || {};
  const automation = pick(result, "automation");

  const ewmFit = pick(
    sap,
    "ewm_fit_signals",
    "fit_indicators",
    "ewm_indicators",
    "ewm_fit"
  );
  const customDev = pick(
    sap,
    "custom_development_signals",
    "custom_dev_required",
    "custom_dev_signals"
  );
  const integrationComplexity = pick(
    sap,
    "integration_complexity_signals",
    "integration_complexity"
  ) || pick(complexity, "integration_complexity");
  const automationSignals = pick(sap, "automation_signals") || automation;
  const multiSiteSignals =
    pick(sap, "multi_site_complexity_signals", "multi_site_complexity") ||
    pick(complexity, "multi_site_complexity");

  const blocks = [
    { label: "EWM Fit Signals", value: ewmFit },
    { label: "Custom Development Signals", value: customDev },
    { label: "Integration Complexity Signals", value: integrationComplexity },
    { label: "Automation Signals", value: automationSignals },
    { label: "Multi-Site Complexity Signals", value: multiSiteSignals },
  ].filter((b) => !isEmpty(b.value));

  // Vendor signals: array of qualifications — surface top items.
  const vendorList = pick(vendor, "qualifications", "must_haves", "signals");

  if (blocks.length === 0 && isEmpty(vendorList)) return null;

  return (
    <SectionShell id="signals" icon={Brain} title="SAP Solution Signals">
      <p className="text-[11px] text-[#6B7280] mb-4">
        Extracted detection signals only — not final recommendations.
      </p>
      <div className="space-y-4">
        {blocks.map((b) => (
          <SignalBlock key={b.label} label={b.label} value={b.value} />
        ))}
        {!isEmpty(vendorList) && (
          <div>
            <p className="text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1.5">
              Vendor Qualification Signals
            </p>
            <BulletList
              testId="rfp-priva-vendor-signals"
              items={normalizeToStrings(vendorList)}
              max={8}
            />
          </div>
        )}
      </div>
    </SectionShell>
  );
}

function SignalBlock({ label, value }) {
  const testId = `rfp-priva-signal-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  if (typeof value === "boolean") {
    return (
      <div data-testid={testId}>
        <p className="text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1">
          {label}
        </p>
        <StatusPill label={value ? "Detected Signal" : "Not Detected"} tone={value ? "accent" : "outOfScope"} />
      </div>
    );
  }
  if (Array.isArray(value)) {
    return (
      <div data-testid={testId}>
        <p className="text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1.5">
          {label}
        </p>
        <BulletList items={normalizeToStrings(value)} max={8} />
      </div>
    );
  }
  if (typeof value === "object") {
    const entries = Object.entries(value).filter(([k, v]) => !HIDDEN_KEYS.has(k) && !isEmpty(v));
    if (entries.length === 0) return null;
    return (
      <div data-testid={testId}>
        <p className="text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1.5">
          {label}
        </p>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-2">
          {entries.map(([k, v]) => (
            <Field
              key={k}
              label={humanize(k)}
              value={normaliseDetailValue(v)}
            />
          ))}
        </dl>
      </div>
    );
  }
  return (
    <div data-testid={testId}>
      <p className="text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide mb-1">
        {label}
      </p>
      <p className="text-sm text-[#1F2937] leading-relaxed">{String(value)}</p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Bottom actions — review + convert                                          */
/* -------------------------------------------------------------------------- */
function BottomActions({
  assessmentId,
  reviewStatus,
  conversionStatus,
  customerResponses,
  onReviewUpdated,
  onConverted,
}) {
  const [saving, setSaving] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const reviewed = reviewStatus === "reviewed";
  const isConverted = conversionStatus === "converted";
  const hasCustomerResponses = !!(
    customerResponses &&
    Array.isArray(customerResponses.rows) &&
    customerResponses.rows.length > 0
  );

  const toggleReview = async () => {
    if (!assessmentId) return;
    const next = reviewed ? "not_reviewed" : "reviewed";
    setSaving(true);
    try {
      await patchRfpSubmission(assessmentId, { extraction_review_status: next });
      onReviewUpdated?.(next);
      toast.success(
        next === "reviewed"
          ? "Extraction marked as reviewed."
          : "Extraction reverted to not reviewed."
      );
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to update extraction status.";
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target?.files?.[0];
    if (!file || !assessmentId) return;
    setUploading(true);
    try {
      const res = await uploadRfpCustomerResponses(assessmentId, file);
      const a = res.answer_count || 0;
      const c = res.clarification_count || 0;
      const u = res.unmatched_count || 0;
      toast.success(
        `Customer responses received — ${a} mapped answer${a === 1 ? "" : "s"}` +
          (c ? `, ${c} clarification${c === 1 ? "" : "s"}` : "") +
          (u ? `, ${u} unmatched row${u === 1 ? "" : "s"}` : "") +
          ". Re-Convert to apply these as missing-data additions."
      );
      onConverted?.();
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to upload customer responses.";
      toast.error(detail);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleClearResponses = async () => {
    if (!assessmentId) return;
    try {
      await clearRfpCustomerResponses(assessmentId);
      toast.success(
        "Customer responses cleared. The next Re-Convert will use PRIVA extraction only."
      );
      onConverted?.();
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to clear customer responses.";
      toast.error(detail);
    }
  };

  let convertLabel = "Create Bid Analysis";
  if (isConverted) convertLabel = "Re-Create Bid Analysis";

  return (
    <div
      data-testid="rfp-priva-bottom-actions"
      className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm px-5 py-4 sm:px-6 sm:py-5"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-2.5 min-w-0 flex-wrap">
          <BadgeCheck
            className={reviewed ? "w-4 h-4 text-[#065F46]" : "w-4 h-4 text-[#9CA3AF]"}
            strokeWidth={1.75}
          />
          <span className="text-[12px] text-[#4B5563]">Review status:</span>
          <StatusPill
            label={reviewed ? "Reviewed" : "Not Reviewed"}
            tone={reviewed ? "inScope" : "outOfScope"}
          />
          {isConverted && (
            <span
              data-testid="rfp-conversion-status-pill"
              className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wide rounded-sm px-2 py-0.5 border bg-[#ECFDF5] text-[#065F46] border-[#065F46]/15"
            >
              Structure Converted
            </span>
          )}
          {hasCustomerResponses && (
            <span
              data-testid="rfp-customer-responses-pill"
              className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wide rounded-sm px-2 py-0.5 border bg-[#EFF6FF] text-[#1E40AF] border-[#1E40AF]/15"
              title={`Uploaded ${customerResponses.uploaded_at || ""}`}
            >
              Customer Responses Staged
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            data-testid="rfp-priva-mark-reviewed-btn"
            onClick={toggleReview}
            disabled={saving || !assessmentId}
            className={[
              "h-9 px-4 text-xs font-semibold rounded-sm transition-colors shadow-sm disabled:opacity-50",
              reviewed
                ? "bg-white text-[#374151] hover:bg-[#F8FAFC] border border-[#D1D5DB]"
                : "bg-[#025979] hover:bg-[#03738C] text-white",
            ].join(" ")}
          >
            <BadgeCheck className="mr-1.5 w-3.5 h-3.5" strokeWidth={1.75} />
            {saving
              ? "Saving…"
              : reviewed
              ? "Mark Not Reviewed"
              : "Mark Extraction Reviewed"}
          </Button>
          <Button
            data-testid="rfp-priva-convert-btn"
            onClick={() => setConvertOpen(true)}
            disabled={!reviewed}
            className="bg-[#8B5CF6] hover:bg-[#7C3AED] text-white h-9 px-4 text-xs font-semibold rounded-sm transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#8B5CF6]"
            title={reviewed ? "Open conversion preview" : "Mark the extraction reviewed first"}
          >
            <ArrowRight className="mr-1.5 w-3.5 h-3.5" strokeWidth={1.75} />
            {convertLabel}
          </Button>
        </div>
      </div>

      {/* Post-conversion: upload customer-filled questionnaire for additive
          re-conversion. Hidden until the assessment has been converted at
          least once so the consultant doesn't upload responses to a stale
          structure. */}
      {isConverted && (
        <div
          data-testid="rfp-customer-responses-panel"
          className="mt-4 pt-4 border-t border-[#E5E7EB] flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3"
        >
          <div className="text-[12px] text-[#4B5563] leading-relaxed flex-1 min-w-0">
            <strong className="text-[#111827]">Have customer responses?</strong>{" "}
            Upload the filled questionnaire Excel — answers will be added
            <em> additively</em> to the PRIVA extraction (only filling in
            cells that were missing or default). Existing extracted answers
            and your overrides are preserved.
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <input
              ref={fileInputRef}
              type="file"
              data-testid="rfp-customer-responses-input"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={handleUpload}
              className="hidden"
            />
            <Button
              data-testid="rfp-customer-responses-upload-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="bg-white text-[#025979] hover:bg-[#E6F1F5] border border-[#025979] h-8 px-3 text-xs font-semibold rounded-sm transition-colors disabled:opacity-50"
            >
              <Upload className="mr-1.5 w-3.5 h-3.5" strokeWidth={1.75} />
              {uploading
                ? "Uploading…"
                : hasCustomerResponses
                ? "Replace Customer Responses"
                : "Upload Customer Responses (.xlsx)"}
            </Button>
            {hasCustomerResponses && (
              <Button
                data-testid="rfp-customer-responses-clear-btn"
                onClick={handleClearResponses}
                className="bg-white text-[#991B1B] hover:bg-[#FEE2E2] border border-[#991B1B]/30 h-8 px-2.5 text-xs font-medium rounded-sm transition-colors"
                title="Remove the staged customer responses"
              >
                Clear
              </Button>
            )}
          </div>
        </div>
      )}

      <RfpConversionPreview
        open={convertOpen}
        onOpenChange={setConvertOpen}
        assessmentId={assessmentId}
        onConverted={onConverted}
      />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Top-level panel                                                            */
/* -------------------------------------------------------------------------- */
export default function PrivaExtractedIntelligence({
  result,
  assessmentId,
  reviewStatus,
  conversionStatus,
  customerResponses,
  onReviewUpdated,
  onConverted,
}) {
  const hasContent = useMemo(() => {
    if (!result || typeof result !== "object") return false;
    return Object.keys(result).some(
      (k) => !HIDDEN_KEYS.has(k) && !isEmpty(result[k])
    );
  }, [result]);

  if (!hasContent) {
    return (
      <div
        data-testid="rfp-priva-intelligence-empty"
        className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm px-5 py-4"
      >
        <p className="text-sm text-[#6B7280]">
          PRIVA returned an empty extraction result. Try re-running the
          analysis.
        </p>
      </div>
    );
  }

  return (
    <div
      data-testid="rfp-priva-intelligence-panel"
      className="space-y-4"
    >
      {/* Panel header — kept brand-light per the "minimal purple" rule. */}
      <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm">
        <div className="px-5 py-4 sm:px-6 sm:py-5 border-b border-[#E5E7EB] flex items-center gap-2 flex-wrap">
          <div className="w-8 h-8 rounded-sm bg-[#F3E8FF] border border-[#6D28D9]/15 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-[#6D28D9]" strokeWidth={1.75} />
          </div>
          <h3
            className="text-sm font-semibold text-[#111827] uppercase tracking-wider"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            PRIVA Extracted Intelligence
          </h3>
          <span className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wide rounded-sm px-2 py-0.5 bg-[#F3E8FF] text-[#6D28D9] border border-[#6D28D9]/15">
            AI
          </span>
          <span className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wide rounded-sm px-2 py-0.5 bg-[#F3F4F6] text-[#6B7280] border border-[#E5E7EB]">
            Read-only
          </span>
        </div>
        <div className="px-5 py-3.5 sm:px-6 flex items-start gap-2.5 bg-[#F8FAFC]">
          <Info
            className="w-4 h-4 text-[#025979] mt-0.5 shrink-0"
            strokeWidth={1.5}
          />
          <p
            data-testid="rfp-priva-intelligence-note"
            className="text-[12px] text-[#4B5563] leading-relaxed"
          >
            PRIVA has extracted and structured key RFP information. Review and
            confirm the extracted intelligence before creating the Bid Analysis.
          </p>
        </div>
      </div>

      <SnapshotSection result={result} />
      <ScopeSection result={result} />
      <OpenQuestionsSection result={result} />
      <RisksAssumptionsSection result={result} />
      <OperationsSection result={result} />
      <VolumesSection result={result} />
      <IntegrationSection result={result} />
      <TechnicalSection result={result} />
      <SignalsSection result={result} />

      <BottomActions
        assessmentId={assessmentId}
        reviewStatus={reviewStatus}
        conversionStatus={conversionStatus}
        customerResponses={customerResponses}
        onReviewUpdated={onReviewUpdated}
        onConverted={onConverted}
      />
    </div>
  );
}
